import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react';
import { Menu, MenuItem } from '../primitives/menu.tsx';
import { cn } from '../util/classes.ts';

/**
 * The floating surface around `primitives/menu` — a menu that opens from a
 * trigger, with the keyboard behaviour a menu is expected to have (arrow keys,
 * typeahead, Escape, focus return) taken from Base UI rather than written here.
 *
 * Only the surface parts are wrapped. The rows and everything in them stay the
 * menu primitives: import `MenuItemLeading`, `MenuItemLabel`, `MenuItemTrailing`,
 * `MenuGroup` and `MenuDivider` from `primitives/menu` and compose them inside
 * `DropdownMenuItem` the same way you would in any other menu.
 *
 * ```tsx
 * <DropdownMenu>
 *     <DropdownMenuTrigger render={<Button>Neu</Button>} />
 *     <DropdownMenuContent align="start">
 *         <DropdownMenuItem onClick={createTask}>
 *             <MenuItemLeading>
 *                 <CheckSquareIcon />
 *             </MenuItemLeading>
 *             <MenuItemLabel>Aufgabe</MenuItemLabel>
 *         </DropdownMenuItem>
 *     </DropdownMenuContent>
 * </DropdownMenu>
 * ```
 */
export type DropdownMenuProps = {
    children?: ReactNode;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    modal?: MenuPrimitive.Root.Props['modal'];
};

export function DropdownMenu({
    children,
    open,
    defaultOpen,
    onOpenChange,
    modal = false,
}: DropdownMenuProps): ReactElement {
    return (
        <MenuPrimitive.Root
            open={open}
            defaultOpen={defaultOpen}
            // Base UI hands its own event details as a second argument; callers
            // only ever want the open state, so it stops here.
            onOpenChange={(next) => {
                onOpenChange?.(next);
            }}
            modal={modal}
        >
            {children}
        </MenuPrimitive.Root>
    );
}

export type DropdownMenuTriggerProps = ComponentPropsWithRef<'button'> & {
    /** The element to render as the trigger — usually a `Button`. */
    render?: ReactElement;
};

export function DropdownMenuTrigger({ render, children, ...props }: DropdownMenuTriggerProps): ReactElement {
    return (
        <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" render={render} {...props}>
            {children}
        </MenuPrimitive.Trigger>
    );
}

export type DropdownMenuContentProps = Omit<ComponentPropsWithRef<'ul'>, 'children'> &
    Pick<MenuPrimitive.Popup.Props, 'children' | 'finalFocus'> &
    Pick<
        MenuPrimitive.Positioner.Props,
        'align' | 'alignOffset' | 'anchor' | 'collisionPadding' | 'side' | 'sideOffset'
    >;

/**
 * The popup itself. Base UI's popup already carries `role="menu"`, so it is
 * rendered *as* the `Menu` list rather than wrapped around one — one element,
 * one role.
 */
export function DropdownMenuContent({
    align = 'start',
    alignOffset = 0,
    anchor,
    children,
    className,
    collisionPadding = 8,
    finalFocus,
    side = 'bottom',
    sideOffset = 6,
    ...props
}: DropdownMenuContentProps): ReactElement {
    return (
        <MenuPrimitive.Portal>
            <MenuPrimitive.Positioner
                data-slot="dropdown-menu-positioner"
                className="isolate z-50"
                align={align}
                alignOffset={alignOffset}
                anchor={anchor}
                collisionPadding={collisionPadding}
                side={side}
                sideOffset={sideOffset}
            >
                <MenuPrimitive.Popup
                    data-slot="dropdown-menu-content"
                    finalFocus={finalFocus}
                    render={
                        // `outline-none`: the popup takes focus on open, so the
                        // browser would otherwise paint a ring around the whole
                        // list. The highlighted row is the focus indicator here.
                        <Menu
                            className={cn(
                                'motion-popup',
                                'max-h-(--available-height) min-w-32 overflow-y-auto',
                                'origin-(--transform-origin) rounded-default border border-line-weak',
                                'bg-container text-content shadow-lg outline-none',
                                className,
                            )}
                            {...props}
                        />
                    }
                >
                    {children}
                </MenuPrimitive.Popup>
            </MenuPrimitive.Positioner>
        </MenuPrimitive.Portal>
    );
}

export type DropdownMenuItemProps = ComponentPropsWithRef<'button'> & {
    /** Whether picking the row closes the menu. @default true */
    closeOnClick?: boolean;
};

/**
 * One row. Compose its content from the `primitives/menu` parts —
 * `<MenuItemLabel>…</MenuItemLabel>` carries the visible text. Both the accessible
 * name and Base UI's typeahead fall back to that rendered text, so no `label` prop
 * is needed here.
 */
export function DropdownMenuItem({
    closeOnClick,
    disabled,
    children,
    className,
    ...props
}: DropdownMenuItemProps): ReactElement {
    return (
        <MenuPrimitive.Item
            closeOnClick={closeOnClick}
            disabled={disabled}
            render={
                <MenuItem
                    className={cn(
                        'focus-visible:outline-none data-highlighted:bg-content/(--opacity-wash)',
                        className,
                    )}
                    {...props}
                >
                    {children}
                </MenuItem>
            }
        />
    );
}
